import styled from "@emotion/styled"
import { IconButton } from "@mui/material"
import Image from "next/image"
import { Dispatch, SetStateAction, useState } from "react"
import { Desktop, Mobile } from "ui"

interface VideoControlIcon {
  default: string
  hover: string
}

interface VideoControls {
  desktop: {
    play: VideoControlIcon
    pause: VideoControlIcon
    volumeOn: VideoControlIcon
    volumeOff: VideoControlIcon
  }
  mobile: {
    play: VideoControlIcon
    pause: VideoControlIcon
    volumeOn: VideoControlIcon
    volumeOff: VideoControlIcon
  }
}

export const videoControls: VideoControls = {
  desktop: {
    play: {
      default: "/icons/videoControls/pauseBig.svg",
      hover: "/icons/videoControls/playBigHover.svg",
    },
    pause: {
      default: "/icons/videoControls/playBig.svg",
      hover: "/icons/videoControls/pauseBigHover.svg",
    },
    volumeOn: {
      default: "/icons/videoControls/vOnBig.svg",
      hover: "/icons/videoControls/vOnBigHover.svg",
    },
    volumeOff: {
      default: "/icons/videoControls/vOffBig.svg",
      hover: "/icons/videoControls/vOffBigHover.svg",
    },
  },
  mobile: {
    play: {
      default: "/icons/videoControls/pauseSmall.svg",
      hover: "/icons/videoControls/playSmallHover.svg",
    },
    pause: {
      default: "/icons/videoControls/playSmall.svg",
      hover: "/icons/videoControls/pauseSmallHover.svg",
    },
    volumeOn: {
      default: "/icons/videoControls/vOnSmall.svg",
      hover: "/icons/videoControls/vOnSmallHover.svg",
    },
    volumeOff: {
      default: "/icons/videoControls/vOffSmall.svg",
      hover: "/icons/videoControls/vOffSmallHover.svg",
    },
  },
}

interface VideoControlButtonProps {
  icon: {
    default: string
    hover: string
  }
  onClick: () => void
  ariaLabel: string
  size: number
}

export function VideoControlButton({
  icon,
  onClick,
  ariaLabel,
  size,
}: VideoControlButtonProps) {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <StyledIconButton
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label={ariaLabel}
    >
      <Image
        src={isHovered ? icon.hover : icon.default}
        alt={ariaLabel}
        width={size}
        height={size}
      />
    </StyledIconButton>
  )
}

const StyledLayout = styled.div`
  display: flex;
  gap: 24px;
  z-index: 10000;
  align-self: end;
  height: fit-content;
  margin-inline-start: auto;
  ${({ theme }) => theme.breakpoints.up("md")} {
    margin: unset;
    padding-bottom: 32px;
    gap: 32px;
  }
`

const StyledIconButton = styled(IconButton)`
  width: 32px;
  height: 32px;
  background-color: white !important;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 40px;
    height: 40px;
  }
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.16);
`

type Props = {
  playing: boolean
  setPlaying: Dispatch<SetStateAction<boolean>>
  volume: boolean
  setVolume: Dispatch<SetStateAction<boolean>>
}

export default function VideoControls({
  playing,
  setPlaying,
  volume,
  setVolume,
}: Props) {
  const onPlayPause = () => {
    setPlaying((el) => !el)
  }
  const onVolumeToggle = () => {
    setVolume((el) => !el)
  }

  return (
    <>
      <Desktop>
        <StyledLayout>
          <VideoControlButton
            icon={
              playing ? videoControls.desktop.pause : videoControls.desktop.play
            }
            onClick={onPlayPause}
            ariaLabel={playing ? "Pause" : "Play"}
            size={24}
          />
          <VideoControlButton
            icon={
              volume
                ? videoControls.desktop.volumeOn
                : videoControls.desktop.volumeOff
            }
            onClick={onVolumeToggle}
            ariaLabel={volume ? "Unmute" : "Mute"}
            size={24}
          />
        </StyledLayout>
      </Desktop>

      <Mobile zIndex={10}>
        <StyledLayout>
          <VideoControlButton
            icon={
              playing ? videoControls.mobile.pause : videoControls.mobile.play
            }
            onClick={onPlayPause}
            ariaLabel={playing ? "Pause" : "Play"}
            size={16}
          />
          <VideoControlButton
            icon={
              volume
                ? videoControls.mobile.volumeOn
                : videoControls.mobile.volumeOff
            }
            onClick={onVolumeToggle}
            ariaLabel={volume ? "Unmute" : "Mute"}
            size={16}
          />
        </StyledLayout>
      </Mobile>
    </>
  )
}
